import { watch, onUnmounted } from 'vue'
import { Terminal, AppEventBackupService } from 'app-core'
import { useAppStore } from '@/stores/useAppStore'

// ── AppEvent backup poll ──
//
// 60s reliability backstop under the best-effort LAN/WAN transports: re-fetches
// AppEvents this terminal may have missed and re-feeds them through the unchanged
// AppEvent.handle() funnel (dedup + dispatch). See docs/AppEventBackupPoll.md.
//
// Started once from MainLayout setup — the post-login terminal-session lifecycle,
// same as useOrdersHubIngestion — so mount/unmount is the natural on/off switch.
export function useAppEventBackupPoll() {
  const store = useAppStore()

  const POLL_INTERVAL_MS = 60 * 1000
  // Offset from the OrdersHub 60s poll so the two pollers don't fire on the same tick.
  const PHASE_OFFSET_MS = 30 * 1000

  let interval: ReturnType<typeof setInterval> | null = null
  let startTimeout: ReturnType<typeof setTimeout> | null = null

  // Gate the FETCH per tick, not the timer. `Terminal.all()` is an ORM query, not
  // a reactive flag, so it can't be watched — instead the timer always runs for an
  // authenticated session and each tick checks the roster first. A single-terminal
  // venue keeps the (idle) timer but makes ZERO network calls. This also self-heals
  // the first-cold-login race: the first tick that sees >1 terminal starts polling.
  function pollIfMultiTerminal() {
    if (Terminal.all().length > 1) AppEventBackupService.poll()
  }

  // Phase-offset start: first poll at +30s, then every 60s.
  startTimeout = setTimeout(() => {
    pollIfMultiTerminal()
    interval = setInterval(pollIfMultiTerminal, POLL_INTERVAL_MS)
  }, PHASE_OFFSET_MS)

  // Re-poll once on an offline→online transition — a reconnect likely missed events.
  watch(() => store.isOnline, (online, wasOnline) => {
    if (online && wasOnline === false) pollIfMultiTerminal()
  })

  onUnmounted(() => {
    if (startTimeout) { clearTimeout(startTimeout); startTimeout = null }
    if (interval) { clearInterval(interval); interval = null }
  })
}
